import { uuidv7 } from "uuidv7"
import {
  CanvasEdges,
  CanvasNodes,
  SetEdgesState,
  SetNodesState,
  UICell,
  UIColumn,
  UIEdge,
  UILogic,
  UISheet,
  UISheetType,
} from "../types/UITypes"
import { getId } from "./common"
import { Edge, Node } from "reactflow"
import { Dispatch, SetStateAction } from "react"
import { SupabaseConnector } from "../database/SupabaseConnector"
import { CELL_WIDTH } from "./constants"

export const getSheetDimensions = (cells: UICell[]) => {
  let sheetRows = 1
  let sheetColumns = 1

  cells.forEach((cell) => {
    if (cell.rowIndex > sheetRows) {
      sheetRows = cell.rowIndex
    }
    if (cell.columnIndex > sheetColumns) {
      sheetColumns = cell.columnIndex
    }
  })

  return [sheetColumns, sheetRows]
}

export const insertRow = (
  cells: UICell[],
  rowIndex: number,
  sheetId: string
) => {
  const [sheetColumns, sheetRows] = getSheetDimensions(cells)

  const newCells = Array(sheetColumns)
    .fill(null)
    .map(
      (_, i) =>
        ({
          id: uuidv7(),
          rowIndex,
          columnIndex: i + 1,
          sheetId,
        } as UICell)
    )

  // Append row
  if (rowIndex > sheetRows) {
    return [cells.concat(newCells), newCells]
  }

  // Insert row
  return [
    cells
      .map((cell) =>
        cell.rowIndex < rowIndex
          ? cell
          : { ...cell, rowIndex: cell.rowIndex + 1 }
      )
      .concat(newCells),
    newCells,
  ]
}

export const insertColumn = (
  sheet: UISheet,
  columnIndex: number,
  sheetId: string
) => {
  const { cells, columns = [] } = sheet
  const [sheetColumns, sheetRows] = getSheetDimensions(cells)
  const newColumn = {
    id: getId(),
    columnIndex,
    sheetId,
    width: CELL_WIDTH,
  }

  const newCells = Array(sheetRows)
    .fill(null)
    .map(
      (_, i) =>
        ({
          id: getId(),
          columnIndex,
          rowIndex: i + 1,
          sheetId,
        } as UICell)
    )

  // Append column
  if (columnIndex > sheetColumns) {
    return {
      updatedCells: cells.concat(newCells),
      newCells,
      updatedColumns: columns,
      newColumn,
    }
  }

  // Insert column
  const updatedCells = cells
    .map((cell) =>
      cell.columnIndex < columnIndex
        ? cell
        : { ...cell, columnIndex: cell.columnIndex + 1 }
    )
    .concat(newCells)

  const updatedColumns = columns.map((column) =>
    column.columnIndex < columnIndex
      ? column
      : { ...column, columnIndex: column.columnIndex + 1 }
  )

  return { updatedCells, newCells, updatedColumns, newColumn }
}

export const BACKGROUND_DOTS_GAP = 16

export const getDraggedNodePosition = (node: Node) => {
  // Looks like there is some kind of internal logic of the ReactFlow that uses those offsets.
  // TODO: find out if they're exposed in the ReactFlow API.
  const GRID_X_OFFSET = 4
  const GRID_Y_OFFSET = -2
  const nodeGridStepsX = Math.round(node.position.x / BACKGROUND_DOTS_GAP)
  const nodeX = nodeGridStepsX * BACKGROUND_DOTS_GAP + GRID_X_OFFSET
  const nodeGridStepsY = Math.round(node.position.y / BACKGROUND_DOTS_GAP)
  const nodeY = nodeGridStepsY * BACKGROUND_DOTS_GAP - GRID_Y_OFFSET
  const updatedNode = {
    ...node,
    position: { x: nodeX, y: nodeY },
  }

  return updatedNode
}

type SetPendingExtendSheetParamsType = Dispatch<
  SetStateAction<
    | {
        sheetId: string
        rowIndex?: number
        columnIndex?: number
      }
    | undefined
  >
>

export async function addRow(
  sheetId: string,
  rowIndex: number,
  nodes: CanvasNodes,
  edges: CanvasEdges,
  setNodes: SetNodesState,
  setEdges: SetEdgesState,
  setPendingApiResponse: Dispatch<SetStateAction<boolean>>,
  isPendingNodesUpdate: boolean,
  setPendingExtendSheetParams: SetPendingExtendSheetParamsType,
  supabaseConnector: SupabaseConnector
) {
  const sheet: UISheet = nodes.find((node) => node.data.id === sheetId)
    ?.data as UISheet

  if (!sheet) {
    return Promise.resolve()
  }

  setPendingApiResponse(true)

  if (isPendingNodesUpdate) {
    setPendingExtendSheetParams({ sheetId, rowIndex })
    return
  }

  const [updatedCells, newCells] = insertRow(sheet.cells, rowIndex + 1, sheetId)

  const updatedNodes = nodes.map((node) =>
    node.data.id !== sheetId || node.id.startsWith("logic")
      ? node
      : {
          ...node,
          data: {
            ...node.data,
            cells: updatedCells,
            // TODO: fix FlowChart types
            // @ts-ignore
            edgeHandles: node.data.edgeHandles.map((edgeHandle) =>
              edgeHandle.rowIndex! < rowIndex
                ? edgeHandle
                : { ...edgeHandle, rowIndex: edgeHandle.rowIndex! + 1 }
            ),
          },
        }
  )

  const updatedEdges: Edge<UIEdge>[] = []

  setNodes(updatedNodes)

  setEdges(
    edges.map((edge) => {
      if (
        edge.data.sourceSheetId !== sheetId ||
        edge.data.sourceRowIndex < rowIndex
      ) {
        return edge
      }

      const updatedEdge = {
        ...edge,
        data: {
          ...edge.data,
          sourceRowIndex: edge.data.sourceRowIndex + 1,
        },
      }

      updatedEdges.push(updatedEdge)

      return updatedEdge
    })
  )

  await supabaseConnector.createCells(newCells, [])

  // Update rowIndex of the cells below the inserted row
  await supabaseConnector.updateCells(
    updatedCells.filter((cell) => cell.rowIndex > rowIndex)
  )

  // Update rowIndex of the edges below the inserted row
  await supabaseConnector.updateEdges(updatedEdges)

  setPendingApiResponse(false)
}

export async function addColumn(
  sheetId: string,
  columnIndex: number,
  nodes: CanvasNodes,
  setNodes: SetNodesState,
  isPendingNodesUpdate: boolean,
  setPendingApiResponse: Dispatch<SetStateAction<boolean>>,
  setPendingExtendSheetParams: SetPendingExtendSheetParamsType,
  supabaseConnector: SupabaseConnector
) {
  const sheet: UISheet = nodes.find((node) => node.data.id === sheetId)
    ?.data as UISheet

  if (!sheet) {
    return Promise.resolve()
  }

  setPendingApiResponse(true)

  if (isPendingNodesUpdate) {
    setPendingExtendSheetParams({ sheetId, columnIndex })
    return
  }

  const { updatedCells, newCells, updatedColumns, newColumn } = insertColumn(
    sheet,
    sheet.entityType === UISheetType.MATRIX ? columnIndex : columnIndex + 1,
    sheetId
  )

  const updatedNodes = nodes.map((node) =>
    node.data.id !== sheetId
      ? node
      : {
          ...node,
          data: {
            ...node.data,
            cells: updatedCells,
            columns: [...updatedColumns, newColumn],
          },
        }
  )

  setNodes(updatedNodes)

  await supabaseConnector.createCells(newCells, [newColumn])

  // Update columnIndex of the cells right to the inserted column
  // TODO: use rpc
  await supabaseConnector.updateCells(
    updatedCells.filter((cell) => cell.columnIndex > columnIndex)
  )

  setPendingApiResponse(false)
}

export async function addCells(
  sheetId: string,
  cells: UICell[],
  columns: UIColumn[],
  nodes: CanvasNodes,
  setNodes: SetNodesState,
  supabaseConnector: SupabaseConnector
) {
  const updatedNodes = nodes.map((node) =>
    node.data.id !== sheetId
      ? node
      : {
          ...node,
          data: {
            ...node.data,
            cells: [
              ...(node.data as UISheet).cells,
              ...cells.map((cell) => ({ ...cell, isNew: false })),
            ],
            columns: [...(node.data as UISheet).columns, ...columns],
          },
        }
  )

  setNodes(updatedNodes)
  await supabaseConnector.createCells(cells, columns)
}

export function pasteCells(
  sheetId: string,
  pastedCells: Record<string, string>,
  setNodes: SetNodesState,
  supabaseConnector: SupabaseConnector
) {
  const cellsToUpdate: UICell[] = []

  setNodes((nodes) =>
    nodes.map((node) =>
      node.data.id !== sheetId
        ? node
        : {
            ...node,
            data: {
              ...node.data,
              cells: (node.data as UISheet).cells.map((cell) => {
                const pastedCell =
                  pastedCells[`${cell.rowIndex}-${cell.columnIndex}`]

                if (pastedCell) {
                  const updatedCell = {
                    ...cell,
                    name: pastedCell,
                  }
                  cellsToUpdate.push(updatedCell)

                  return updatedCell
                }

                return cell
              }),
            },
          }
    )
  )

  if (cellsToUpdate.length) {
    supabaseConnector.updateCells(cellsToUpdate)
  }
}

const getConnectedSheets = (
  node: Node<UISheet | UILogic>,
  sheets: Node<UISheet>[],
  edges: Edge[],
  logics: Node<UILogic>[],
  connectedNodes: Node<UISheet | UILogic>[]
): Node<UISheet | UILogic>[] => {
  let incomingEdges

  if (node.type === "sheet") {
    incomingEdges = edges.filter(
      (edge) => edge.data.targetSheetId === node.data.id
    )
  } else {
    incomingEdges = edges.filter(
      (edge) => edge.data.targetLogicId === node.data.id
    )
  }

  incomingEdges = incomingEdges ?? []

  const incomingSheets: Node<UISheet>[] = []
  const visitedSheets: Record<string, boolean> = {}

  incomingEdges.forEach((edge) => {
    const sheet = sheets.find(
      (sheet) => sheet.data.id === edge.data.sourceSheetId
    )

    if (sheet && !visitedSheets[sheet.id]) {
      visitedSheets[sheet.id] = true
      incomingSheets.push(sheet)
    }
  })

  const incomingLogics: Node<UILogic>[] = []
  const visitedLogics: Record<string, boolean> = {}

  incomingEdges.forEach((edge) => {
    const logic = logics.find(
      (logic) => logic.data.id === edge.data.sourceLogicId
    )
    if (logic && !visitedLogics[logic.id]) {
      visitedLogics[logic.id] = true
      incomingLogics.push(logic)
    }
  })

  const incomingNodes = [...incomingSheets, ...incomingLogics]

  if (incomingNodes?.length) {
    const result = incomingNodes.map((node) =>
      // weird bug - this ts error is not shown in VSCode, figure out, why.
      // @ts-ignore
      getConnectedSheets(node!, sheets, edges, logics, [
        ...connectedNodes,
        ...incomingNodes,
      ])
    )
    return result.flat()
  } else {
    return [...connectedNodes, node]
  }
}

export const getClusters = (nodes: Node<any>[], edges: Edge<any>[]) => {
  const sheets = nodes.filter(
    (node) =>
      node.type === UISheetType.SHEET || node.type === UISheetType.CONDITION
  )
  const logics = nodes.filter((node) => node.type === "logic")

  let apiClusters = sheets
    .filter(
      (node) => !edges.some((edge) => edge.data.sourceSheetId === node.data.id)
    )
    .map((sheet) => getConnectedSheets(sheet, sheets, edges, logics, [sheet]))

  apiClusters = apiClusters.map((clusterNodes) => {
    const clustersMap: Record<string, boolean> = {}
    const clusterNodesFiltered: Node<UISheet | UILogic>[] = []

    clusterNodes.forEach((node) => {
      if (!clustersMap[node.id]) {
        clustersMap[node.id] = true
        clusterNodesFiltered.push(node)
      }
    })

    return clusterNodesFiltered
  })

  const mergedClusters: Node<UISheet | UILogic>[][] = []

  apiClusters.forEach((cluster) => {
    let merged = false

    for (const mergedCluster of mergedClusters) {
      if (cluster.some((node) => mergedCluster.includes(node))) {
        mergedCluster.push(
          ...cluster.filter((node) => !mergedCluster.includes(node))
        )
        merged = true
        break
      }
    }

    if (!merged) {
      mergedClusters.push(cluster)
    }
  })

  return mergedClusters
}
